import { action, computed, makeObservable, observable } from "mobx";
import { UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { DbRecordingEntryWriter } from "../../State/EvidenceDB/DbRecordingEntryWriter";
import { SingleOriginFilterState } from "./SingleOriginFilterState";
import { lazyObservable } from "mobx-utils";
import { SearchUiState } from "./SearchTermUiState";
import { GlobalContext } from "./GlobalContext";
import { getTrackingDomainReportState } from "./getTrackingDomainReportState";
import { getTrackingCodeReportState } from "./getTrackingCodeReportState";
import { JsonOverlayViewParameters } from "./JsonOverlayView";
import { EventsTableWorkerResultsState } from "./EventTableWorkerResultsState";
import { FilteredChronologicalResultsObserver } from "./FilteredChronologicalSearchResultsObserver";
import { DbEventCacheForRecording, GetsDbEventsByIndex } from "../../State/EvidenceDB/DbEventCache";
import { DbEventsForRequestCache } from "../../State/EvidenceDB/DbEventsForRequestCache";
import { GetsDbEventsByRequestId } from "../../State/EvidenceDB/GetsDbEventsByRequestId";
import { ExtensionSettingsAccessor } from "../../State/EvidenceDB/DbExtensionSettings";
import { CallStackResolutionDefault } from "../../lib/Causation/CallStackResolution";
import { DomainSuffixRestriction, checkPermissionOrDomainSuffixRestriction } from "../../State/EvidenceDB/License";
import { LicensePermissions } from "../../State/EvidenceDB/LicensePermissions";

export const ReportIds = [
	'Chronological',
	'Search',
	'DataRecipients',
	'IP',
	'TrackingCode',
	'Analytics',
] as const;
export type ReportId = NonNullable<typeof ReportIds[number]>;

export const PrimaryTabIds = [
	...ReportIds
] as const;
export type PrimaryTabId = NonNullable<typeof PrimaryTabIds[number]>;
export const PrimaryTabLabels = {
	Chronological: 'chronological events',
	Search: 'data sent',
	DataRecipients: 'data recipients',
	IP: 'ip addresses',
	TrackingCode: 'tracking code',
	Analytics: 'analytics',
} as const satisfies Record<PrimaryTabId, string>;
export const ReportName = {
	Chronological: 'Events Observed in Chronological Order',
	Search: 'Data Sent',
	DataRecipients: 'Data Recipients',
	IP: 'IP Addresses Transmitted',
	TrackingCode: 'Tracking Code',
	Analytics: 'Analytics',
} as const satisfies Record<ReportId, string>;


const ReportIdSet = new Set(ReportIds);
export const isReportId = (candidate: unknown): candidate is ReportId => ReportIdSet.has(candidate as ReportId);
 const PrimaryTabIdSet = new Set(PrimaryTabIds);
 export const isPrimaryTabId = (candidate: unknown): candidate is PrimaryTabId => PrimaryTabIdSet.has(candidate as PrimaryTabId);

 export interface RecordingPanelPrimaryTabState {
	readonly primaryTabId: PrimaryTabId;
	setPrimaryTab: (tab: string | undefined) => void;
 }


export class RecordingPanelState implements JsonOverlayViewParameters, RecordingPanelPrimaryTabState {
	readonly searchUiState: SearchUiState;
	readonly sourcePageUrlFilterState: SingleOriginFilterState;
	readonly filteredChronologicalResultsObserver: FilteredChronologicalResultsObserver;

	readonly globalContext: GlobalContext;
	@computed get db() { return this.globalContext.db; }

	_primaryTabId: PrimaryTabId | undefined;
	@computed get primaryTabId(): PrimaryTabId { return isPrimaryTabId(this._primaryTabId) ? this._primaryTabId :
			this.globalContext.extensionSettings.settings.searchInRealTime ? 'Chronological' : 'Analytics' }
	setPrimaryTab = action ( (tab: string | undefined) => {
		if (isPrimaryTabId(tab)) {
			this._primaryTabId = tab;
		}
	});
	setPrimaryTabFn = (tab: PrimaryTabId) => () => this.setPrimaryTab(tab);

	_objectToDisplayAsJson: unknown | undefined;
	@computed public get objectToDisplayAsJson() { return this._objectToDisplayAsJson; }
	setObjectToDisplayAsJson = action ( (json: unknown | undefined) => {
		this._objectToDisplayAsJson = json;
	});

	@computed get dbRecordingEntryWriter() {
		return DbRecordingEntryWriter.fromRecordingUuidDeferredLoad(this.db, this.recordingUuid);
	}
	@computed get recording() { return this.dbRecordingEntryWriter?.recording; }
	@computed get recordingName() { return this.dbRecordingEntryWriter?.name; }
	@computed get recordingNameOrDescription() { return this.dbRecordingEntryWriter?.nameOrDescription; }
	setRecordingName = (name: string) => this.dbRecordingEntryWriter?.setName(name);

	@computed get recordingStatus() { return this.recording?.status; }
	@computed get callStackResolution() {
		return this.recording?.callStackResolution ?? 
			ExtensionSettingsAccessor.current?.settings.callStackResolution ??
			CallStackResolutionDefault;
	}
	
	@computed get inProgress() {
		return this.recordingStatus === 'recording';
	}

	@computed get reportFullName() {
		return `${this.recordingNameOrDescription} ${ReportName[this.primaryTabId]}`;
	}

	@computed get eventsTableAnalysisResults(): EventsTableWorkerResultsState {
		return EventsTableWorkerResultsState.forRecordingUuidAndSearchTerms(this.recordingUuid, this.searchUiState.searchTerms, this.globalContext);
	}

	@computed get cookieStats() {
		return this.eventsTableAnalysisResults.cookiesObserver;
	}

	@computed get sourceDestinationStats() {
		return this.eventsTableAnalysisResults.originAndDestinationUrlsObserver;
	}

	@computed get searchResultsObserver() {
		return this.eventsTableAnalysisResults.searchResultsObserver;
	}

	@computed get causationGraphObserver() {
		return this.eventsTableAnalysisResults.causationGraphObserver;
	}

	@computed get sourcePageDomains() {
		return this.eventsTableAnalysisResults.originAndDestinationUrlsObserver.sourcePageDomains;
	}

	@computed get licenseCache() {
		return this.globalContext.licenseCache;
	}
	@computed get licensePermissions(): LicensePermissions {
		return this.licenseCache;
	}

	permissionsForSourcePageDomains = (domainSuffixRestriction: DomainSuffixRestriction | boolean, requireEveryDomainToBeLicensed: boolean = false)  =>{
		if (domainSuffixRestriction == null) return true;
		if (!domainSuffixRestriction) return false;
		return requireEveryDomainToBeLicensed ? 
			this.sourcePageDomains.every( pageDomain =>
				checkPermissionOrDomainSuffixRestriction(domainSuffixRestriction, {pageDomain})
			) :
			this.sourcePageDomains.some( pageDomain =>
				checkPermissionOrDomainSuffixRestriction(domainSuffixRestriction, {pageDomain})
			);		
	}

	@computed get mayExportSomeData(): boolean {
		return this.permissionsForSourcePageDomains(this.licenseCache.mayExportData, true);
	}

	@computed get mayExportAllData(): boolean {
		return this.permissionsForSourcePageDomains(this.licenseCache.mayExportData, true);
	}

	@computed get maySaveReports(): boolean {
		return this.permissionsForSourcePageDomains(this.licenseCache.maySaveReports);
	}

	@computed get mayGenerateIpReports(): boolean {
		return this.permissionsForSourcePageDomains(this.licenseCache.mayGenerateIpReports);
	}

	@computed get trackingSummaryReportState() {
		return getTrackingDomainReportState(this.eventsTableAnalysisResults);
	}

	@computed get trackingCodeReportState() {
		return getTrackingCodeReportState(this.eventsTableAnalysisResults);
	}

//	public readonly globalContext: GlobalContext;
	public readonly recordingUuid: UUID;

	public requestGetter: GetsDbEventsByRequestId;
	public getsDbEventsByIndex: GetsDbEventsByIndex;

	constructor({globalContext, recordingUuid}: {readonly globalContext: GlobalContext, readonly recordingUuid: UUID}) {
		this.globalContext = globalContext;
		this.recordingUuid = recordingUuid;
		this.searchUiState = new SearchUiState(recordingUuid,  {...this.globalContext,
			getDomainOrUrlToFilterFor: () => this.sourcePageUrlFilterState.domainOrUrlToFilterFor,
			getSourceDestinationUrlStats: () => this.eventsTableAnalysisResults.originAndDestinationUrlsObserver
		});
		this.sourcePageUrlFilterState = new SingleOriginFilterState(
				() => this.eventsTableAnalysisResults.originAndDestinationUrlsObserver.sourcePageOriginStatisticsTree
			);
		this.filteredChronologicalResultsObserver = new FilteredChronologicalResultsObserver({
			getSearchUiState: () => this.searchUiState,
			getSearchResultsState: () => this.searchResultsObserver,
			getReportableEventsState: () => this.eventsTableAnalysisResults.nonSearchChronologicalEventsObserver
		});
		this.requestGetter = new DbEventsForRequestCache(this.recordingUuid);
		this.getsDbEventsByIndex = new DbEventCacheForRecording(this.recordingUuid);
		makeObservable(this, {
			_primaryTabId: observable, _objectToDisplayAsJson: observable
		});
	}

	static #forRecordingUuid = new Map<UUID, RecordingPanelState>();
	static forRecordingUuidAsync = async (globalContext: GlobalContext, recordingUuid: UUID) => {
		const existingRecordingPanelState = RecordingPanelState.#forRecordingUuid.get(recordingUuid)
		if (existingRecordingPanelState != null) {
			return existingRecordingPanelState;
		}
		const dbRecordingEntryWriter = await DbRecordingEntryWriter.fromRecordingUuidAwaitLoad(globalContext.db, recordingUuid);
		if (dbRecordingEntryWriter == null) { 
			throw new Error(`No recording found for recordingUuid ${recordingUuid}`);
		}
		const recordingPanelState = new RecordingPanelState({globalContext, recordingUuid});
		RecordingPanelState.#forRecordingUuid.set(recordingUuid, recordingPanelState);
		return recordingPanelState
	}

	static forRecordingUuid(globalContext: GlobalContext, recordingUuid: UUID | undefined ) {
		if (recordingUuid == null) return undefined;
		return RecordingPanelState.#forRecordingUuid.get(recordingUuid) ??
			lazyObservable<RecordingPanelState | undefined>( async (sink) => {
				try {
					sink(await RecordingPanelState.forRecordingUuidAsync(globalContext, recordingUuid));
				} catch (e) {
					console.error(e);
					sink(undefined);
				}
			}).current();
	}
}

